import { getSupabase } from './client';
import { pickDisplayName } from './auth';
import { getOrRefreshProfile } from '../googleAuth';
import { t } from '../language';
import { cleanProfileText, isPasswordStrong, validateAvatarDataUrl } from '../profileRules';
import type { GoogleProfile, PasswordChangeInput, ProfileDTO, ProfileUpdate } from '../../shared/types';

/**
 * Kullanıcının kendi profili (Ayarlar > Genel > Profil).
 *
 * Yazma yetkisi veritabanında kolon düzeyinde sınırlı (bkz.
 * migrations/0015_profile_settings.sql): bu yoldan yalnızca ad, ünvan,
 * şirket ve fotoğraf değişebilir; rol ve kota yazılamaz.
 */

interface ProfileRow {
  display_name: string | null;
  pronouns: string | null;
  department: string | null;
  job_title: string | null;
  company: string | null;
  avatar_url: string | null;
}

export async function getProfile(): Promise<ProfileDTO> {
  const { userId, email } = await sessionUser();
  const [row, google, hasPassword] = await Promise.all([
    readRow(userId),
    getOrRefreshProfile(),
    accountHasPassword(),
  ]);
  return toDTO(row, email || google?.email || '', google, hasPassword);
}

/** Verilen alanları kaydeder, güncel profili döndürür. */
export async function updateProfile(patch: ProfileUpdate): Promise<ProfileDTO> {
  const input = (patch ?? {}) as ProfileUpdate;
  const update: Partial<ProfileRow> = {};

  if (input.displayName !== undefined) {
    const name = cleanProfileText(input.displayName);
    if (!name) throw new Error(t('Ad boş bırakılamaz.', 'Name cannot be empty.'));
    update.display_name = name;
  }
  if (input.pronouns !== undefined) update.pronouns = cleanProfileText(input.pronouns) || null;
  if (input.department !== undefined) update.department = cleanProfileText(input.department) || null;
  if (input.jobTitle !== undefined) update.job_title = cleanProfileText(input.jobTitle) || null;
  if (input.company !== undefined) update.company = cleanProfileText(input.company) || null;
  if (input.avatarUrl !== undefined) {
    update.avatar_url = input.avatarUrl === null ? null : validateAvatarDataUrl(input.avatarUrl);
  }

  const { userId } = await sessionUser();
  if (Object.keys(update).length > 0) {
    const { data, error } = await getSupabase()
      .from('profiles')
      .update(update)
      .eq('id', userId)
      .select('id');
    if (error) {
      console.error('[profile] güncellenemedi:', error.message);
      throw new Error(t('Profil kaydedilemedi. Tekrar deneyin.', 'Could not save your profile. Please try again.'));
    }
    if (!data || data.length === 0) {
      throw new Error(t('Profil kaydı bulunamadı.', 'Profile record not found.'));
    }
  }
  return getProfile();
}

async function sessionUser(): Promise<{ userId: string; email: string }> {
  const { data } = await getSupabase().auth.getSession();
  const user = data.session?.user;
  if (!user) {
    throw new Error(t('Oturum bulunamadı — lütfen tekrar giriş yapın.', 'No active session — please sign in again.'));
  }
  return { userId: user.id, email: user.email ?? '' };
}

async function readRow(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await getSupabase()
    .from('profiles')
    .select('display_name, pronouns, department, job_title, company, avatar_url')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.error('[profile] okunamadı:', error.message);
    throw new Error(t('Profil yüklenemedi.', 'Could not load your profile.'));
  }
  return (data as ProfileRow | null) ?? null;
}

function toDTO(
  row: ProfileRow | null,
  email: string,
  google: GoogleProfile | undefined,
  hasPassword: boolean
): ProfileDTO {
  return {
    email,
    displayName: pickDisplayName(row?.display_name, email, google?.name),
    pronouns: row?.pronouns ?? '',
    department: row?.department ?? '',
    jobTitle: row?.job_title ?? '',
    company: row?.company ?? '',
    avatarUrl: row?.avatar_url ?? null,
    hasPassword,
    googleName: google?.name,
    googleAvatarDataUrl: google?.pictureDataUrl,
  };
}

/**
 * Hesapta e-posta + şifre kimliği var mı?
 *
 * Masaüstünde giriş Google ile yapılıyor; böyle açılmış bir hesabın şifresi
 * hiç olmayabilir. O durumda şifre penceresi "mevcut şifre" sormaz. Bilgi
 * sunucudan okunuyor, ağ yoksa yerel oturumdaki kimliklere bakılıyor.
 */
async function accountHasPassword(): Promise<boolean> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.getUser();
  const user = error ? (await supabase.auth.getSession()).data.session?.user : data.user;
  return (user?.identities ?? []).some((identity) => identity.provider === 'email');
}

/**
 * Şifre belirler ya da değiştirir.
 *
 * Hesabın şifresi varsa önce mevcut şifre doğrulanıyor (aynı hesapta yeniden
 * giriş yapılarak); yoksa doğrudan yeni şifre yazılıyor. Kural denetimi
 * arayüzde de var, burada tekrar ediliyor — IPC ucu doğrudan çağrılabilir.
 */
export async function changePassword(input: PasswordChangeInput): Promise<ProfileDTO> {
  const newPassword = typeof input?.newPassword === 'string' ? input.newPassword : '';
  if (!isPasswordStrong(newPassword)) {
    throw new Error(
      t(
        'Şifre kurallara uymuyor: en az 8 karakter, bir büyük harf, bir küçük harf ve bir rakam.',
        'The password does not meet the rules: at least 8 characters, one uppercase letter, one lowercase letter and one number.'
      )
    );
  }

  const supabase = getSupabase();
  const { email } = await sessionUser();

  if (await accountHasPassword()) {
    const currentPassword = typeof input.currentPassword === 'string' ? input.currentPassword : '';
    if (!currentPassword) {
      throw new Error(t('Mevcut şifreni gir.', 'Enter your current password.'));
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
    if (error) {
      throw new Error(t('Mevcut şifre yanlış.', 'Your current password is incorrect.'));
    }
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) {
    console.error('[profile] şifre değiştirilemedi:', error.message);
    const message = error.message.toLowerCase();
    if (message.includes('different from the old password')) {
      throw new Error(t('Yeni şifre eskisiyle aynı olamaz.', 'The new password must be different from the old one.'));
    }
    if (message.includes('weak') || message.includes('password should be')) {
      throw new Error(t('Bu şifre çok zayıf. Daha güçlü bir şifre seçin.', 'That password is too weak. Choose a stronger one.'));
    }
    if (message.includes('reauthentication')) {
      throw new Error(
        t(
          'Güvenlik için yeniden giriş yapman gerekiyor. Çıkış yapıp tekrar giriş yaptıktan sonra dene.',
          'For security you need to sign in again. Sign out, sign back in and retry.'
        )
      );
    }
    throw new Error(t('Şifre değiştirilemedi. Tekrar deneyin.', 'Could not change the password. Please try again.'));
  }

  // Şifre az önce belirlendi; kimlik listesi henüz tazelenmemiş olabilir.
  return { ...(await getProfile()), hasPassword: true };
}
